import { apiSlice } from '../apiSlice';
import { Locale } from '../../i18n/init';
import { ResourceReference, UserResourceReference } from '../../types';

type Resource = {
  code: string;
  locale: Locale;
  copy: number;
};

interface CreateResponse {
  userResourceReference: ResourceReference;
  message: string;
}

interface UpdateUserResourceRef {
  result: UserResourceReference;
  message: string;
}

/** API Endpoints related to User Resources and User Resource references */
export const userResourceApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    /** 
    * createUserResourceReference mutation (POST)
    * @param {Partial<Resource>} - The resource to be duplicated and converted to a user resource.
    * @returns {CreateResponse} the created user resource.
    */
    createUserResourceReference: build.mutation<
      CreateResponse,
      Partial<Resource>
    >({
      query: (body) => {
        return {
          url: `user-resource/create`,
          method: 'POST',
          body
        };
      }
    }),

    /** 
    * getUserResourceReference query (GET)
    * @param {string} code - The user resource's project code
    * @param {Locale} locale - The locale of the user resource
    * @param {number} copy - The copy number of the resource
    * @returns {UserResourceReference} the fetched user resource reference.
    */
    getUserResourceReference: build.query<
      UserResourceReference,
      { code: string; locale: Locale; copy: number }
    >({
      query: (args) => {
        const { code, locale, copy } = args;
        return {
          url: `user-resource/get-reference/${code}/${locale}/${copy}`
        };
      },
      providesTags: ['UserResourceRef']
    }),
    
    /** 
    * getForUser query (GET)
    * @returns {UserResourceReference[]} the user resources created from the current logged in user.
    */
    getForUser: build.query<UserResourceReference[], void>({
      query: () => `user-resource/get-for-user`,
      transformResponse: (response: {
        userResourceRefs: UserResourceReference[];
      }) => response.userResourceRefs
    }),

    /** 
    * getUserResource query (GET)
    * @param {string} code - The user resource's project code
    * @param {Locale} locale - The locale of the user resource
    * @param {number} copy - The copy number of the resource
    * @returns {UserResourceReference} the fetched user resource.
    */
    getUserResource: build.query<
      { userResource: any },
      { code: string; locale: Locale; copy: number }
    >({
      query: (args) => {
        const { code, locale, copy } = args;
        return {
          url: `user-resource/get/${code}/${locale}/${copy}`
        };
      }
    }),

    /** 
    * updateUserResourceReference mutation (POST)
    * @param {Partial<UserResourceReference>} - The new user resource reference item.
    * @returns {UpdateUserResourceRef} the updated user resource reference.
    */
    updateUserResourceReference: build.mutation<
      UpdateUserResourceRef,
      Partial<UserResourceReference>
    >({
      query: (body) => {
        return {
          url: `user-resource/save-resource-ref/`,
          method: 'POST',
          body: body
        };
      },
      invalidatesTags: ['UserResourceRef']
    })
  })
});

export const {
  useGetUserResourceReferenceQuery,
  useCreateUserResourceReferenceMutation,
  useUpdateUserResourceReferenceMutation,
  useGetUserResourceQuery,
  useGetForUserQuery
} = userResourceApiSlice;
